import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { socketService } from '../../../services/socket';

interface BanksEditorProps {
    externalStaff: any[];
    externalParticipants: any[];
    externalBanks: any[];
    externalGroups: any[];
}

export const BanksEditor: React.FC<BanksEditorProps> = ({ externalStaff, externalParticipants, externalBanks, externalGroups }) => {
    const { t } = useTranslation();
    const [selectedLoggerId, setSelectedLoggerId] = useState<string | null>(null);
    const [cameras, setCameras] = useState<any[]>([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [viewMode, setViewMode] = useState<'individual' | 'global'>('individual');

    // Bank State for selected logger
    const [assignedCameraIds, setAssignedCameraIds] = useState<string[]>([]);
    const [assignedParticipantIds, setAssignedParticipantIds] = useState<string[]>([]);

    useEffect(() => {
        socketService.emit('get-tags-data', (data: any) => {
            if (data && data.cameras) {
                setCameras(data.cameras);
            }
        });
    }, []);

    // Load bank when logger changes
    useEffect(() => {
        if (selectedLoggerId) {
            const bank = externalBanks.find(b => b.userId === selectedLoggerId);
            if (bank) {
                try {
                    setAssignedCameraIds(JSON.parse(bank.cameras || '[]'));
                    setAssignedParticipantIds(JSON.parse(bank.participants || '[]'));
                } catch (e) {
                    console.error('Error parsing bank data', e);
                    setAssignedCameraIds([]);
                    setAssignedParticipantIds([]);
                }
            } else {
                setAssignedCameraIds([]);
                setAssignedParticipantIds([]);
            }
        }
    }, [selectedLoggerId, externalBanks]);

    const handleSave = () => {
        if (!selectedLoggerId) return;
        console.log('💾 Saving bank for:', selectedLoggerId, assignedCameraIds, assignedParticipantIds);
        socketService.emit('save-bank', {
            userId: selectedLoggerId,
            cameras: assignedCameraIds,
            participants: assignedParticipantIds
        });
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
    };

    const toggleCamera = (id: string) => {
        setAssignedCameraIds(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
    };

    const toggleParticipant = (id: string) => {
        setAssignedParticipantIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
    };

    const toggleGroup = (groupId: string) => {
        const groupMembers = externalParticipants.filter(p => p.groupId === groupId).map(p => p.id);
        const allAssigned = groupMembers.every(id => assignedParticipantIds.includes(id));

        if (allAssigned) {
            // Unassign all members of this group
            setAssignedParticipantIds(prev => prev.filter(id => !groupMembers.includes(id)));
        } else {
            // Assign all members of this group
            setAssignedParticipantIds(prev => {
                const newIds = [...prev];
                groupMembers.forEach(id => {
                    if (!newIds.includes(id)) newIds.push(id);
                });
                return newIds;
            });
        }
    };

    const loggers = externalStaff.filter(s => s.role === 'Logger');

    return (
        <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', color: 'var(--color-text-primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ color: 'var(--color-accent-danger)', margin: 0, letterSpacing: '2px' }}>{t('controller.banks.title')}</h2>
                <div style={{ display: 'flex', background: 'var(--color-panel-bg)', borderRadius: 'var(--border-radius-md)', padding: '4px', border: '1px solid var(--color-panel-border)' }}>
                    <button
                        onClick={() => setViewMode('individual')}
                        style={{
                            padding: '8px 16px', borderRadius: 'var(--border-radius-sm)', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem',
                            background: viewMode === 'individual' ? 'var(--color-accent-danger)' : 'transparent',
                            color: viewMode === 'individual' ? '#fff' : 'var(--color-text-muted)'
                        }}
                    >
                        {t('controller.banks.individual')}
                    </button>
                    <button
                        onClick={() => setViewMode('global')}
                        style={{
                            padding: '8px 16px', borderRadius: 'var(--border-radius-sm)', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem',
                            background: viewMode === 'global' ? 'var(--color-accent-danger)' : 'transparent',
                            color: viewMode === 'global' ? '#fff' : 'var(--color-text-muted)'
                        }}
                    >
                        {t('controller.banks.globalView')}
                    </button>
                </div>
            </div>

            {viewMode === 'individual' ? (
                <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: 0 }}>
                    {/* LIST OF LOGGERS */}
                    <div style={{ width: '250px', display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', borderRight: '1px solid var(--color-panel-border)', paddingRight: '20px' }}>
                        <h3 style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>{t('controller.banks.selectLogger')}</h3>
                        {loggers.map(l => (
                            <div
                                key={l.id}
                                onClick={() => setSelectedLoggerId(l.id)}
                                style={{
                                    padding: '10px',
                                    background: selectedLoggerId === l.id ? 'rgba(247, 45, 96, 0.2)' : 'var(--color-card-bg)',
                                    border: selectedLoggerId === l.id ? '1px solid var(--color-accent-danger)' : '1px solid var(--color-panel-border)',
                                    borderRadius: 'var(--border-radius-sm)',
                                    cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s'
                                }}
                            >
                                <img src={l.image} style={{ width: '30px', height: '30px', borderRadius: '50%' }} />
                                <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{l.name}</span>
                            </div>
                        ))}
                    </div>

                    {/* ASSIGNMENT AREA */}
                    {selectedLoggerId ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ margin: 0 }}>{t('controller.banks.editing')} {loggers.find(l => l.id === selectedLoggerId)?.name}</h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {showSuccess && <span style={{ color: 'var(--color-accent-success)', fontWeight: 'bold' }}>{t('controller.banks.saved')}</span>}
                                    <button onClick={handleSave} style={{ background: 'var(--color-accent-success)', color: '#000', border: 'none', padding: '10px 20px', borderRadius: 'var(--border-radius-sm)', fontWeight: 'bold', cursor: 'pointer' }}>{t('controller.banks.saveChanges')}</button>
                                </div>
                            </div>

                            {/* CAMERAS */}
                            <div style={{ background: 'var(--color-panel-bg)', padding: '15px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-panel-border)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h4 style={{ margin: 0, color: 'var(--color-accent-warning)' }}>{t('controller.banks.assignedCameras')}</h4>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={() => setAssignedCameraIds(cameras.map(c => c.id))}
                                            style={{ background: 'var(--color-input-bg)', color: 'var(--color-text-muted)', border: '1px solid var(--color-panel-border)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}
                                        >
                                            {t('controller.banks.selectAll')}
                                        </button>
                                        <button
                                            onClick={() => setAssignedCameraIds([])}
                                            style={{ background: 'var(--color-input-bg)', color: 'var(--color-text-muted)', border: '1px solid var(--color-panel-border)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}
                                        >
                                            {t('controller.banks.clear')}
                                        </button>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {cameras.map(cam => {
                                        const isAssigned = assignedCameraIds.includes(cam.id);
                                        return (
                                            <div
                                                key={cam.id}
                                                onClick={() => toggleCamera(cam.id)}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    background: isAssigned ? 'var(--color-accent-warning)' : 'var(--color-input-bg)',
                                                    color: isAssigned ? '#000' : 'var(--color-text-muted)',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    border: '1px solid var(--color-panel-border)',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                {cam.name}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* PARTICIPANTS */}
                            <div style={{ background: 'var(--color-panel-bg)', padding: '20px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-panel-border)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h4 style={{ margin: 0, color: 'var(--color-accent-danger)' }}>{t('controller.banks.assignedParticipants')}</h4>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {/* GROUPS UTILITY */}
                                        {externalGroups.map(group => (
                                            <button
                                                key={group.id}
                                                onClick={() => toggleGroup(group.id)}
                                                style={{
                                                    background: group.color + '22',
                                                    color: group.color,
                                                    border: `1px solid ${group.color}44`,
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.7rem',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {group.name}
                                            </button>
                                        ))}
                                        <div style={{ width: '10px' }} />
                                        <button
                                            onClick={() => setAssignedParticipantIds(externalParticipants.map(p => p.id))}
                                            style={{ background: 'var(--color-input-bg)', color: 'var(--color-text-muted)', border: '1px solid var(--color-panel-border)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}
                                        >
                                            {t('controller.banks.selectAll')}
                                        </button>
                                        <button
                                            onClick={() => setAssignedParticipantIds([])}
                                            style={{ background: 'var(--color-input-bg)', color: 'var(--color-text-muted)', border: '1px solid var(--color-panel-border)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}
                                        >
                                            {t('controller.banks.clear')}
                                        </button>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                                    {externalParticipants.map(p => {
                                        const isAssigned = assignedParticipantIds.includes(p.id);
                                        const group = externalGroups.find(g => g.id === p.groupId);
                                        return (
                                            <div
                                                key={p.id}
                                                onClick={() => toggleParticipant(p.id)}
                                                style={{
                                                    padding: '8px 12px',
                                                    borderRadius: '6px',
                                                    background: isAssigned ? 'var(--color-accent-danger)' : 'var(--color-input-bg)',
                                                    color: isAssigned ? '#fff' : 'var(--color-text-muted)',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    border: group ? `1px solid ${group.color}66` : '1px solid var(--color-panel-border)',
                                                    fontSize: '0.85rem',
                                                    display: 'flex', alignItems: 'center', gap: '8px'
                                                }}
                                            >
                                                {p.image && <img src={p.image} style={{ width: '22px', height: '22px', borderRadius: '50%' }} />}
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <span>{p.name}</span>
                                                    {group && <span style={{ fontSize: '0.6rem', color: group.color }}>{group.name}</span>}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', fontSize: '1.2rem', letterSpacing: '2px' }}>
                            {t('controller.banks.selectLoggerToConfigure')}
                        </div>
                    )}
                </div>
            ) : (
                /* GLOBAL OVERVIEW MODE */
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--color-bg-base)', borderRadius: 'var(--border-radius-lg)', overflow: 'hidden', border: '1px solid var(--color-panel-border)' }}>
                        <thead>
                            <tr style={{ background: 'var(--color-panel-bg)', textAlign: 'left' }}>
                                <th style={{ padding: '15px', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{t('controller.banks.logger')}</th>
                                <th style={{ padding: '15px', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{t('controller.banks.cameras')}</th>
                                <th style={{ padding: '15px', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{t('controller.banks.participants')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loggers.map(l => {
                                const bank = externalBanks.find(b => b.userId === l.id);
                                const bankCams = bank ? JSON.parse(bank.cameras || '[]') : [];
                                const bankParts = bank ? JSON.parse(bank.participants || '[]') : [];

                                return (
                                    <tr key={l.id} style={{ borderBottom: '1px solid var(--color-panel-border)' }}>
                                        <td style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <img src={l.image} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid var(--color-panel-border)' }} />
                                            <span style={{ fontWeight: 'bold' }}>{l.name}</span>
                                        </td>
                                        <td style={{ padding: '15px' }}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                {bankCams.length === 0 ? <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>-</span> : (
                                                    bankCams.map((camId: string) => {
                                                        const cam = cameras.find(c => c.id === camId);
                                                        return <span key={camId} style={{ fontSize: '0.7rem', background: '#ff993322', color: 'var(--color-accent-warning)', padding: '2px 6px', borderRadius: '4px', border: '1px solid #ff993344' }}>{cam?.name || camId}</span>
                                                    })
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '15px' }}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                {bankParts.length === 0 ? <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>-</span> : (
                                                    bankParts.map((partId: string) => {
                                                        const part = externalParticipants.find(p => p.id === partId);
                                                        return <span key={partId} style={{ fontSize: '0.7rem', background: '#f72d6022', color: 'var(--color-accent-danger)', padding: '2px 6px', borderRadius: '4px', border: '1px solid #f72d6044' }}>{part?.name || partId}</span>
                                                    })
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
